/**
 * Format independent drivers for Model API export
 * 
 * Transforms an array of independent driver objects into a columnar format
 * suitable for the R API export endpoint.
 * 
 * @param independentDrivers - Array of driver objects from artifact.independent_drivers
 * @returns Formatted object with columnar data structure
 */

interface Driver {
  id?: number
  name?: string
  source?: string
  updated_at?: string
  units?: string
  quality?: string
  [key: string]: any
}

interface FormattedDriversForExport {
  Driver: string[]
  Source: string[]
  'Last Update': string[]
  Units: string[]
  Quality: string[]
}

/**
 * Get quality display string with colored bullet point indicator
 */
const getQualityDisplay = (quality?: string): string => {
  if (!quality) return '● Good' // Default to Good if not specified
  
  const qualityLower = quality.toLowerCase()
  
  if (qualityLower === 'good') {
    return '● Good'
  } else if (qualityLower === 'check' || qualityLower === 'warning') {
    return '● Check'
  } else if (qualityLower === 'bad' || qualityLower === 'error') {
    return '● Bad'
  }
  
  // Default fallback
  return '● Good'
}

/**
 * Format date to YYYY-MM-DD format
 */
const formatDate = (dateString?: string): string => {
  if (!dateString) {
    // Return current date if not specified
    const now = new Date()
    return now.toISOString().split('T')[0]
  }
  
  try {
    const date = new Date(dateString)
    return date.toISOString().split('T')[0]
  } catch (error) {
    // Fallback to current date if parsing fails
    const now = new Date()
    return now.toISOString().split('T')[0]
  }
}

/**
 * Main formatting function
 */
export const formatIndependentDriversForModelAPI = (
  independentDrivers?: Driver[] | readonly Driver[]
): FormattedDriversForExport | null => {
  // Return null if no drivers provided
  if (!independentDrivers || independentDrivers.length === 0) {
    return null
  }

  const formatted: FormattedDriversForExport = {
    Driver: [],
    Source: [],
    'Last Update': [],
    Units: [],
    Quality: [],
  }

  // Transform each driver into columnar format
  independentDrivers.forEach((driver) => {
    // Driver name - use name field or id as fallback
    formatted.Driver.push(driver.name || `driver-${driver.id}` || 'unknown')
    
    // Source - use source field or default to 'dframe'
    formatted.Source.push(driver.source || 'dframe')
    
    // Last Update - format the updated_at timestamp
    formatted['Last Update'].push(formatDate(driver.updated_at))
    
    // Units - use units field or default to 'value'
    formatted.Units.push(driver.units || 'value')
    
    // Quality - format with colored bullet point
    formatted.Quality.push(getQualityDisplay(driver.quality))
  })

  return formatted
}

export default formatIndependentDriversForModelAPI
